/**
 * RecentDealsSection — the estate's recorded TRANSACTIONS (SEO G3).
 *
 * The counterpart to `PriceTrendSection` directly above it, and the reason
 * both can sit on one page: that section is what sellers ASK, this one is what
 * buyers PAID. The note under the heading says so in as many words, because
 * these are the two most confusable numbers on a property page and the
 * distinction is not self-evident from the figures.
 *
 * Renders the SHARED transactions table, so a deal shown here and the same
 * deal on `/estates/…/transactions` cannot differ in a column, a unit or a
 * missing value.
 */
import { TransactionsTable } from '@/shared/components/TransactionsTable';
import { Link } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { TransactionRow } from '@/shared/lib/types/transactions';
import { routes } from '@letsgethome/routes';
import { slug, type Locale, type PublicId, type Slug } from '@letsgethome/types/branded';

export function RecentDealsSection({
  deals,
  estateSlug,
  estatePublicId,
  locale,
}: {
  deals: readonly TransactionRow[];
  estateSlug: Slug;
  estatePublicId: PublicId;
  locale: Locale;
}) {
  const t = useTranslations('transactions');

  return (
    <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h2 className="mb-2 text-2xl font-bold">{t('recentDeals')}</h2>
      {/* The one line that keeps this section from being read as the trend. */}
      <p className="mb-6 text-sm text-muted-foreground">{t('recentDealsNote')}</p>
      {/* No estate column: every row is this estate. */}
      <TransactionsTable rows={deals} locale={locale} showEstate={false} />
      <p className="mt-4 text-sm">
        <Link
          href={routes.estates.transactions(slug(`${estateSlug}-${estatePublicId}`))}
          prefetch="intent"
          className="text-success-text underline-offset-2 hover:underline"
        >
          {t('seeAll')}
        </Link>
      </p>
    </section>
  );
}
